import React from "react";
import { Comment } from '@roc-digital/types';
import { useMethod } from "./useMethod";
import { getConfig } from '@roc-digital/mxm-base/config';
import { JsonFetch } from "@roc-digital/mxm-base/logic";

interface UseComments {
  send(body: string)
  pageDown();
  pageUp();
  delete(comment: Comment);
  edit(comment: Comment, body: string);
  comments: Comment[];
  canPageDown?: boolean;
  canPageUp?: boolean;
  isSending?: boolean;
  loading?: boolean;
  loadingUp?: boolean;
  loadingDown?: boolean;
  loaded?: boolean;
}

/**
 * This is hard coded into the backend. We just know that the api will always return pages of 10 comments.
 */
const PAGE_COUNT = 10;

export function useComments(refId: string, replyTo?: number, commentId?: number): UseComments {
  const pendingComments = React.useMemo(() => new Map<string, Comment>(), []);
  const state = React.useMemo(() => {
    return {
      canPageUp: false,
      canPageDown: false,
    }
  }, []);

  const pageDownQuery = useMethod(fetchCommentsDown, {
    autoRun: [refId, commentId || 0, replyTo],
    map(next, current) {
      state.canPageDown = next.result.length >= PAGE_COUNT ? true : false;
      if (next.args[1] === 0) {
        return next;
      }
      return {
        result: [...current?.result || [], ...next.result],
        args: next.args
      };
    },
  });

  const pageUpQuery = useMethod(fetchCommentsUp, {
    autoRun: commentId ? [refId, commentId, replyTo, 1] : undefined,
    map(next, current) {
      state.canPageUp = next.result.length >= PAGE_COUNT || next?.args[3] === 1 ? true : false;
      if (next.args[1] === 0) {
        return next;
      }
      return {
        result: [...next.result, ...current?.result || []],
        args: next.args
      };
    },
  });

  const comments = React.useMemo(() => {
    if(!pageDownQuery.result?.length) return  pageUpQuery.result || [];
    if(!pageUpQuery.result?.length) return  pageDownQuery.result || [];

    return [...pageUpQuery.result, ...pageDownQuery.result].filter((c, i, l) => l.findIndex(f => f.id === c.id) === i);
  }, [pageDownQuery.result, pageUpQuery.result]);

  const createQuery = useMethod(createComment, {
    onBefore: async (args) => {
      const body = args[1];

      const pComment: Comment = {
        id: Math.random(),
        body: body,
        handle: '(Sending)',
        ref_id: '',
        timestamp: Date.now(),
        user_id: '',
        can_edit: true,
        reply_to: replyTo
      }

      pendingComments.set(body, pComment);

      const updates = [
        pComment,
        ...pageDownQuery.getLastResult() || [],
      ];

      pageDownQuery.setResult(updates);

      return args;
    },
    onError: (err, args) => {
      let pComment = pendingComments.get(args[1]);
      const results = pageDownQuery.getLastResult() || [];
      let replaceIndex = -1;

      if (pComment) {
        replaceIndex = results.indexOf(pComment);
        pendingComments.delete(args[0]);
      }

      if(replaceIndex == -1 || !pComment) return;

      pComment = {
        ...pComment,
        handle: 'Failed to send comment!',
        error: 'Reason: ' + (err.data?.message || 'Unknown error'),
      },
      results.splice(replaceIndex, 1, pComment);
      pageDownQuery.setResult([...results]);

    },

    onSuccess: (newComment, args) => {
      const pComment = pendingComments.get(args[1]);
      const results = pageDownQuery.getLastResult() || [];
      let replaceIndex = -1;

      if (pComment) {
        replaceIndex = results.indexOf(pComment);
        pendingComments.delete(args[0])
      }

      if (replaceIndex > -1) {
        results.splice(replaceIndex, 1, newComment);
      } else {
        results.splice(0, 0, newComment);
      }

      pageDownQuery.setResult([...results]);
    }
  });

  const updateCommentInList = (id: number, changes: Partial<Comment>, unlist?: boolean) => {
    const results = pageDownQuery.getLastResult();
    const commentIndex = results.findIndex(c => c.id === id);

    if(unlist) {
      results.splice(commentIndex, 1);
      pageDownQuery.setResult([...results]);
      return;
    }

    if(commentIndex === -1) return;
    const newList = [...results];
    newList[commentIndex] = {
      ...newList[commentIndex],
      ...changes,
    }
    pageDownQuery.setResult(newList);
  }

  const del = (comment: Comment) => {
    updateCommentInList(comment.id, {
      body: '(Comment removed)',
      can_edit: false,
    }, true)
    deleteComment(comment.ref_id, comment.id)
    .catch(err => {
      updateCommentInList(comment.id, {
        error:  'Failed to delete: ' + (err.data?.message || 'Unknown error'),
      });
    });
  }

  const edit = (comment: Comment, body: string) => {
    updateCommentInList(comment.id, {
      body: body,
    })
    editComment(comment.ref_id, comment.id, body)
    .catch(err => {
      updateCommentInList(comment.id, {
        error:  'Failed to update: ' + (err.data?.message || 'Unknown error'),
      });
    });
  }

  return {
    comments: comments,
    pageDown: () => {
      const results = pageDownQuery.getLastResult()
      if (!results.length) {
        pageDownQuery.run(refId, 0, replyTo);
        return;
      }
      const lastId = results[results.length - 1].id;
      pageDownQuery.run(refId, lastId, replyTo)
    },
    pageUp: () => {
      const results = pageUpQuery.getLastResult();
      if (!results.length) {
        pageDownQuery.run(refId, 0, replyTo);
        return;
      }
      const lastId = results[0].id;
      pageUpQuery.run(refId, lastId, replyTo)
    },
    send: (body) => {
      createQuery.run(refId, body, replyTo).catch(_ => {});
    },
    canPageDown: state.canPageDown,
    canPageUp: state.canPageUp,
    isSending: createQuery.loading,
    loadingUp: pageUpQuery.loading,
    loadingDown: pageDownQuery.loading,
    loading: pageDownQuery.loading || pageUpQuery.loading,
    loaded: pageDownQuery.count > 0 || pageUpQuery.count > 0,
    delete: del,
    edit
  }
}

async function createComment(refId: string, body: string, replyTo?: number): Promise<Comment> {
  const baseUrl = getConfig().mxmAuxApiUrl;

  const comment = await JsonFetch<Comment>({
    method: 'POST',
    url: `${baseUrl}/articles/${refId}/comments`,
    data: {
      body,
      reply_to: replyTo
    }
  })

  return comment;
}

async function fetchCommentsDown(refId: string, before: number, replyTo?: number): Promise<Comment[]> {
  const baseUrl = getConfig().mxmAuxApiUrl;

  const comments = await JsonFetch<Comment[]>({
    method: 'GET',
    url: `${baseUrl}/articles/${refId}/comments?before_id=${before || 0}&reply_to=${replyTo || ''}`,
  });

  return comments;
}

async function fetchCommentsUp(refId: string, after: number, replyTo?: number, count?: number): Promise<Comment[]> {
  const baseUrl = getConfig().mxmAuxApiUrl;

  const comments = await JsonFetch<Comment[]>({
    method: 'GET',
    url: `${baseUrl}/articles/${refId}/comments?after_id=${after || 0}&reply_to=${replyTo || ''}&count=${count}`,
  });

  return comments.reverse();
}

async function deleteComment(refId: string, commentId: number): Promise<void> {
  const baseUrl = getConfig().mxmAuxApiUrl;

  await JsonFetch<Comment[]>({
    method: 'DELETE',
    url: `${baseUrl}/articles/${refId}/comments/${commentId}`,
  });
}

async function editComment(refId: string, commentId: number, body: string): Promise<void> {
  const baseUrl = getConfig().mxmAuxApiUrl;

   await JsonFetch<Comment[]>({
    method: 'PATCH',
    url: `${baseUrl}/articles/${refId}/comments/${commentId}`,
    data: {
      body
    }
  });
}